import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, RotateCcw, Loader2, X, Check, Search, Upload } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, fieldStyle, selectStyle, navyBtn, outlineBtn,
} from '../../styles/tokens';
import client from '../../api/client';
import BulkUploadModal from '../shared/BulkUploadModal';

export default function TareasManagement() {
  const [items, setItems]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showAdd, setShowAdd]         = useState(false);
  const [newCode, setNewCode]         = useState('');
  const [newDesc, setNewDesc]         = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [editId, setEditId]           = useState(null);
  const [editCode, setEditCode]       = useState('');
  const [editDesc, setEditDesc]       = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showBulk, setShowBulk]     = useState(false);

  useEffect(() => { fetchItems(); }, []);

  async function fetchItems() {
    try {
      const res = await client.get('/api/admin/cost-codes');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to load cost codes:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (!newCode.trim() || !newDesc.trim()) return;
    setSaving(true);
    setError('');
    try {
      await client.post('/api/admin/cost-codes', {
        code: newCode.trim(),
        description: newDesc.trim(),
        category: newCategory.trim() || null,
      });
      setNewCode('');
      setNewDesc('');
      setNewCategory('');
      setShowAdd(false);
      await fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(id) {
    if (!editCode.trim() || !editDesc.trim()) return;
    try {
      await client.patch(`/api/admin/cost-codes/${id}`, {
        code: editCode.trim(),
        description: editDesc.trim(),
        category: editCategory.trim() || null,
      });
      setEditId(null);
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al actualizar');
    }
  }

  async function handleDelete(id) {
    try {
      await client.delete(`/api/admin/cost-codes/${id}`);
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al desactivar');
    }
  }

  async function handleReactivate(id) {
    try {
      await client.patch(`/api/admin/cost-codes/${id}`, { is_active: true });
      await fetchItems();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al reactivar');
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando tareas…
      </div>
    );
  }

  const term = search.toLowerCase();
  const filteredItems = items.filter(i => {
    const matchesSearch =
      (i.code || '').toLowerCase().includes(term) ||
      (i.description || '').toLowerCase().includes(term);
    if (!matchesSearch) return false;
    if (statusFilter === 'active') return i.is_active;
    if (statusFilter === 'inactive') return !i.is_active;
    return true;
  });
  const activeCount = items.filter(i => i.is_active).length;

  return (
    <div className="page-pad" style={{ padding: '24px 28px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>
          Tareas / Actividades
          <span style={{ fontSize: 13, fontWeight: '400', color: '#94a3b8', marginLeft: 8 }}>
            ({activeCount} activas)
          </span>
        </h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowBulk(true)} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6, background: '#475569' }}>
            <Upload size={14} /> Carga Masiva
          </button>
          <button onClick={() => { setShowAdd(true); setError(''); setNewCode(''); setNewDesc(''); setNewCategory(''); }} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> Agregar Tarea
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por código o descripción…"
            style={{ ...fieldStyle, width: 280, paddingLeft: 30 }}
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...selectStyle, width: 'auto' }}>
          <option value="active">Activas</option>
          <option value="inactive">Inactivas</option>
          <option value="all">Todas</option>
        </select>
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ ...bubbleStyle, marginBottom: 20, animation: 'fadeIn 0.2s ease' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 120px' }}>
              <label style={labelStyle}>Código</label>
              <input
                value={newCode}
                onChange={e => setNewCode(e.target.value)}
                style={fieldStyle}
                placeholder="ej., 100"
                autoFocus
              />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={labelStyle}>Descripción</label>
              <input
                value={newDesc}
                onChange={e => setNewDesc(e.target.value)}
                style={fieldStyle}
                placeholder="ej., Limpieza general"
              />
            </div>
            <div style={{ flex: '0 0 160px' }}>
              <label style={labelStyle}>Categoría</label>
              <input
                value={newCategory}
                onChange={e => setNewCategory(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
                style={fieldStyle}
                placeholder="ej., Mantenimiento"
              />
            </div>
            <button onClick={handleAdd} disabled={saving} style={{ ...navyBtn, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Agregando…' : 'Agregar'}
            </button>
            <button onClick={() => setShowAdd(false)} style={outlineBtn}>Cancelar</button>
          </div>
          {error && (
            <div style={{
              marginTop: 8, padding: '6px 12px', background: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca', borderRadius: 6, fontSize: 12,
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div style={bubbleStyle}>
        {/* Table header */}
        <div style={{
          display: 'grid', gridTemplateColumns: '100px 1fr 150px 80px',
          gap: 8, padding: '8px 0', borderBottom: `2px solid ${BORDER_COLOR}`,
          fontSize: 11, fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em',
        }}>
          <span>Código</span>
          <span>Descripción</span>
          <span>Categoría</span>
          <span style={{ textAlign: 'right' }}>Acciones</span>
        </div>

        {/* Table rows */}
        {filteredItems.map(item => (
          <div key={item.id} style={{
            display: 'grid', gridTemplateColumns: '100px 1fr 150px 80px',
            gap: 8, alignItems: 'center',
            padding: '10px 0', borderBottom: `1px solid ${BORDER_COLOR}`,
            opacity: item.is_active ? 1 : 0.6,
          }}>
            {editId === item.id ? (
              <>
                <input
                  value={editCode}
                  onChange={e => setEditCode(e.target.value)}
                  style={{ ...fieldStyle, fontSize: 13 }}
                  autoFocus
                />
                <input
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  style={{ ...fieldStyle, fontSize: 13 }}
                />
                <input
                  value={editCategory}
                  onChange={e => setEditCategory(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleUpdate(item.id)}
                  style={{ ...fieldStyle, fontSize: 13 }}
                />
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button onClick={() => handleUpdate(item.id)} style={iconBtn} title="Guardar">
                    <Check size={14} style={{ color: '#16a34a' }} />
                  </button>
                  <button onClick={() => setEditId(null)} style={iconBtn} title="Cancelar">
                    <X size={14} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <span style={{
                  fontSize: 13, fontWeight: '600', fontFamily: 'monospace',
                  color: item.is_active ? NAVY : '#94a3b8',
                  textDecoration: item.is_active ? 'none' : 'line-through',
                }}>{item.code}</span>
                <span style={{
                  fontSize: 14, fontWeight: '500',
                  color: item.is_active ? NAVY : '#94a3b8',
                  textDecoration: item.is_active ? 'none' : 'line-through',
                }}>{item.description}</span>
                <span style={{
                  fontSize: 13, color: item.is_active ? '#64748b' : '#94a3b8',
                }}>{item.category || '—'}</span>
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  {item.is_active ? (
                    <>
                      <button onClick={() => {
                        setEditId(item.id);
                        setEditCode(item.code || '');
                        setEditDesc(item.description || '');
                        setEditCategory(item.category || '');
                      }} style={iconBtn} title="Editar">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} style={iconBtn} title="Desactivar">
                        <Trash2 size={13} style={{ color: '#dc2626' }} />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => handleReactivate(item.id)} style={iconBtn} title="Reactivar">
                      <RotateCcw size={13} style={{ color: '#16a34a' }} />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
            No se encontraron tareas
          </div>
        )}
      </div>

      <BulkUploadModal
        open={showBulk}
        onClose={() => setShowBulk(false)}
        onSuccess={fetchItems}
        title="Carga Masiva de Tareas"
        endpoint="/api/admin/cost-codes/bulk"
        templateColumns={['code', 'description', 'category']}
        templateExample={[
          ['03100', 'FORMWORK / ENCOFRADO', 'Concrete'],
          ['07500', 'WATERPROOFING', 'Thermal'],
        ]}
      />
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 4,
};

const iconBtn = {
  background: BG_FIELD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
  padding: '6px 8px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center',
};
